export type PerfilSeccionId = 'sobre-ti' | 'sobre-negocio' | 'sobre-ingresos';

export type PerfilSeccionEstado = 'pendiente' | 'en_proceso' | 'completo';

export type PerfilSeccionAccion = 'continuar' | 'retomar' | 'editar' | 'empezar';

export const PERFIL_SECCION_UBICACION = {
  SOBRE_TI: 'SOBRE_TI',
  SOBRE_NEGOCIO: 'SOBRE_NEGOCIO',
  SOBRE_INGRESOS: 'SOBRE_INGRESOS',
} as const;

export type PerfilSeccionUbicacionCodigo =
  (typeof PERFIL_SECCION_UBICACION)[keyof typeof PERFIL_SECCION_UBICACION];

export const PERFIL_SECCION_UBICACION_OPTIONS: ReadonlyArray<{
  value: PerfilSeccionUbicacionCodigo;
  label: string;
}> = [
  { value: PERFIL_SECCION_UBICACION.SOBRE_TI, label: 'Sobre ti' },
  { value: PERFIL_SECCION_UBICACION.SOBRE_NEGOCIO, label: 'Sobre tu negocio' },
  { value: PERFIL_SECCION_UBICACION.SOBRE_INGRESOS, label: 'Sobre los ingresos' },
];

export const PERFIL_COPY = {
  shell: {
    subtitle:
      'Completa tu perfil para desbloquear ofertas de crédito personalizadas de nuestros aliados financieros.',
  },
  hub: {
    title: 'Cuéntanos sobre ti y tu empresa',
    titleJuridica: 'Cuéntanos sobre la empresa y tu',
    completadoSuffix: 'completado',
    seccionesSuffix: 'secciones',
    solicitudesTitle: 'Tus solicitudes de crédito',
    solicitudesEmpty:
      'Cuando hagas una solicitud de crédito, podrás consultar su estado aquí.',
    ctaCompletarPerfil: 'Completa tu perfil',
    ctaSolicitarCredito: 'Solicitar crédito',
    /** CTA saludo cuando el perfil está al 100 %. */
    ctaEnviarSolicitud: 'Enviar solicitud',
  },
  /** Set de pasos en secciones — mobile accordion */
  stepsStrip: {
    progresoToggle: 'Progreso',
  },
  secciones: {
    'sobre-ti': {
      id: 'sobre-ti' as PerfilSeccionId,
      titulo: 'Sobre ti',
      tituloJuridica: 'Sobre la empresa y tu',
      descripcion:
        'Te pediremos tus datos básicos y tu documento de identidad.',
      descripcionJuridica:
        'Identificación tributaria: Razón social, actividad económica y tipo de sociedad.',
      icon: 'face',
    },
    'sobre-negocio': {
      id: 'sobre-negocio' as PerfilSeccionId,
      titulo: 'Sobre tu negocio',
      descripcion:
        'Cuéntanos dónde opera tu negocio, súbele fotos y, si quieres, redes o documentos opcionales.',
      icon: 'store',
    },
    'sobre-ingresos': {
      id: 'sobre-ingresos' as PerfilSeccionId,
      titulo: 'Sobre los ingresos',
      /** Tradicional / default. */
      descripcion:
        'Queremos entender cómo funciona tu negocio en el día a día.',
      /** HU 8099 Express. */
      descripcionExpress:
        'Queremos saber cómo funciona tu negocio en el día a día. Te pediremos una factura reciente de un proveedor.',
      descripcionJuridica:
        'Estos documentos son los que nos permiten presentarte formalmente ante los bancos aliados.',
      icon: 'attach_money',
    },
  },
  estados: {
    pendiente: 'Pendiente',
    en_proceso: 'En proceso',
    completo: 'Completo',
  } satisfies Record<PerfilSeccionEstado, string>,
  acciones: {
    continuar: 'Continuar',
    retomar: 'Continuar',
    editar: 'Editar',
    empezar: 'Empezar',
  } satisfies Record<PerfilSeccionAccion, string>,
  botones: {
    guardarInformacion: 'Guardar información',
    continuar: 'Continuar',
    saltarPorAhora: 'Saltar por ahora',
    solicitarCredito: 'Solicitar crédito',
  },
  /** Modal al salir con sección incompleta (Saltar / navegación). */
  modalSalir: {
    titulo: '¿Salir sin completar esta sección?',
    cuerpoAntes:
      'Aún tienes información pendiente en esta sección. Si sales ahora, puedes ',
    cuerpoEnfasis: 'guardar los cambios',
    cuerpoDespues: ' y continuar más tarde.',
    ctaGuardar: 'Guardar información',
  },
  /** Modal post-perfil 100 %: pasos siguientes + Revisar / Enviar. */
  modalEnviar: {
    titulo: '¿Qué va a pasar después de enviar tu solicitud?',
    ctaRevisar: 'Revisar información',
    ctaEnviar: 'Enviar solicitud',
    ctaEnviando: 'Enviando…',
    registroIncompleto:
      'Tu registro está incompleto. Actualiza tus datos para poder enviar la solicitud.',
    pasos: [
      {
        antes: 'Tu solicitud será analizada por ',
        enlace: 'aliados financieros',
        despues: '.',
        texto: '',
      },
      {
        antes: '',
        enlace: '',
        despues: '',
        texto:
          'Podrás recibir solicitudes de información adicional o una visita para conocer mejor tu negocio.',
      },
      {
        antes: '',
        enlace: '',
        despues: '',
        texto: 'Te presentaremos una o más propuestas de crédito.',
      },
      {
        antes: '',
        enlace: '',
        despues: '',
        texto:
          'Podrás elegir la opción que mejor se adapte a tu negocio.',
      },
    ],
  },
  footerBar: {
    title: 'Cuéntanos sobre ti y tu empresa',
    titleJuridica: 'Cuéntanos sobre la empresa y tu',
    'sobre-ti':
      'Sobre ti: Te pediremos tus datos básicos y tu documento de identidad.',
    'sobre-ti-juridica':
      'Te pediremos documentos e información adicional',
    'sobre-negocio':
      'Sobre tu negocio: Te pediremos fotos del establecimiento e información adicional.',
    'sobre-ingresos':
      'Sobre los ingresos: Queremos entender cómo funciona tu negocio en el día a día.',
    'sobre-ingresos-express':
      'Sobre los ingresos: Queremos saber cómo funciona tu negocio en el día a día. Te pediremos una factura reciente de un proveedor.',
    'sobre-ingresos-juridica':
      'Te pediremos documentos e información adicional',
  },
  solicitaCredito: {
    titulo: 'Solicita tu crédito',
    footerTitle: 'Define tu necesidad',
    footerSubtitle: 'Dinos cuánto dinero necesitas y para qué lo vas a usar.',
    montoLabel: '¿Cuánto dinero necesitas?',
    montoPlaceholder: 'Ej: $3.000.000',
    plazoLabel: '¿En cuántos meses te gustaría pagarlo?',
    plazoPlaceholder: 'Ej: 16',
    tipoTitulo: 'Tipo de crédito que deseas solicitar',
    tipoIntro:
      'Puedes elegir si deseas recibir ofertas a través de líneas Bancóldex, líneas de aliados financieros o ambas. Esto nos permitirá perfilar mejor las necesidades de tu negocio:',
    tipos: {
      bancoldex: {
        titulo: 'Línea Bancóldex',
        desc:
          'Créditos diseñados por Bancóldex y ofrecidos a través de bancos aliados, con condiciones pensadas para el crecimiento empresarial.',
      },
      aliados: {
        titulo: 'Línea de aliados financieros',
        desc:
          'Créditos ofrecidos directamente por bancos aliados, con condiciones propias de cada entidad.',
      },
      ambas: {
        titulo: 'Ambas opciones',
        desc:
          'Recibe todas las alternativas disponibles y elige la que mejor se adapte a tu negocio.',
      },
    },
    destinoTitulo: '¿En qué vas a usar el crédito?',
    destinoIntro:
      'Selecciona una o varias razones para solicitar el crédito. Puedes agregar un motivo adicional seleccionando “Otro”.',
    destinoOtro: 'Otro:',
    destinoOtroPlaceholder: '',
    validaciones: {
      campoObligatorio: 'Campo es obligatorio',
      destinoRequerido: 'Selecciona al menos una razón',
      otroRequerido: 'Indica el motivo adicional',
    },
  },
  /** Pantalla post-envío exitoso — Figma desktop 152:26601 / mobile 152:26651 */
  solicitudEnviada: {
    titulo: 'Solicitud de crédito enviada',
    /** Mobile */
    subtitulo: 'Tu información ya fue compartida con aliados financieros.',
    /** Desktop */
    subtituloDesktop:
      'Tu perfil ya está en manos de nuestras entidades aliadas para su evaluación.',
    pasosHeading: '¿Qué sigue ahora?',
    ctaInicio: 'Volver al inicio',
    ctaRevisar: 'Revisar solicitud',
    pasos: [
      {
        id: 'analisis',
        titulo: 'Análisis de perfil',
        descripcion:
          'Las entidades revisan tu información según sus políticas de riesgo y viabilidad.',
        descripcionDesktop:
          'Las entidades financieras revisarán tu información bajo sus propias políticas para determinar la viabilidad de una propuesta.',
        icon: 'assets/img/perfil/solicitud-enviada/icon-analisis-perfil.svg',
      },
      {
        id: 'estado',
        titulo: 'Estado de tu proceso',
        descripcion:
          'Te avisaremos por correo o SMS cuando existan actualizaciones de los aliados.',
        descripcionDesktop:
          'Te notificaremos por correo electrónico o mensaje de texto tan pronto tengamos actualizaciones sobre tu solicitud.',
        icon: 'assets/img/perfil/solicitud-enviada/icon-estado-proceso.svg',
      },
      {
        id: 'resultados',
        titulo: 'Revisión de resultados',
        descripcion:
          'Si recibes propuestas, podrás compararlas y elegir la mejor para tu negocio.',
        descripcionDesktop:
          'En caso de recibir propuestas, podrás comparar las condiciones de cada una y decidir cuál se ajusta mejor a tu negocio.',
        icon: 'assets/img/perfil/solicitud-enviada/icon-revision-resultados.svg',
      },
    ] as const,
  },
  modalIntro: {
    ctaCompletarDatos: 'Completar datos',
    tipTitle: 'Ten presente...',
    tipBody:
      'Si te falta algún dato, no te preocupes. Puedes "Guardar y continuar luego"; tu progreso no se perderá.',
    'sobre-ti': {
      titulo: 'Sobre ti',
      intro:
        'Antes de avanzar, asegúrate de tener estos datos a la mano. Es información personal sencilla para validar tu identidad:',
      items: [
        {
          icon: 'id' as const,
          parts: [
            { text: 'Tu documento de identidad', em: true },
            {
              text: ' (lo necesitarás para registrar fechas y subir una foto clara).',
            },
          ],
        },
        {
          icon: 'clipboard' as const,
          parts: [
            { text: 'Información de ' },
            { text: 'seguridad social', em: true },
            { text: ' (si realizas aportes o tienes Sisbén).' },
          ],
        },
        {
          icon: 'people' as const,
          parts: [
            { text: 'Datos de ' },
            { text: 'tu entorno', em: true },
            { text: ': Estado civil y persona a cargo.' },
          ],
        },
      ],
    },
    'sobre-negocio': {
      titulo: 'Sobre tu negocio',
      intro:
        'Queremos conocer dónde ocurre la magia. Para este paso te sugerimos tener:',
      items: [
        {
          icon: 'location' as const,
          parts: [
            { text: 'Ubicación:', em: true },
            {
              text: ' Dirección, barrio y localidad de tu local o punto de operación.',
            },
          ],
        },
        {
          icon: 'document' as const,
          parts: [
            { text: 'Cámara de Comercio o RUT:', em: true },
            {
              text: ' Si los tienes, tenlos cerca (si no, puedes seguir y subirlos después).',
            },
          ],
        },
        // Fotos: las agrega el modal según seccionUbicacion del catálogo.
        {
          icon: 'social' as const,
          parts: [
            { text: 'Redes sociales:', em: true },
            {
              text: ' (Opcional) Si tu negocio tiene Instagram, Facebook o TikTok, ¡queremos verlos!',
            },
          ],
        },
      ],
    },
    'sobre-ingresos': {
      titulo: 'Sobre los ingresos',
      intro:
        'Queremos conocer tu movimiento de caja. Para este paso te sugerimos tener:',
      items: [
        {
          icon: 'accounts' as const,
          parts: [
            { text: 'Tus cuentas claras:', em: true },
            {
              text: ' Un estimado de cuánto vendiste el último año y tus gastos mensuales aproximados.',
            },
          ],
        },
        {
          icon: 'supports' as const,
          parts: [
            { text: 'Soportes a la mano:', em: true },
            {
              text: ' Una factura de proveedor y un recibo de servicios públicos de tu negocio.',
            },
          ],
        },
        {
          icon: 'tax' as const,
          parts: [
            { text: 'Declaración de renta:', em: true },
            {
              text: ' Solo si la presentas, ten el archivo listo para cargarlo.',
            },
          ],
        },
      ],
    },
    /** Intro Express (HU 8099): sin renta / recibo / flujo. */
    'sobre-ingresos-express': {
      titulo: 'Sobre los ingresos',
      intro:
        'Queremos saber cómo funciona tu negocio en el día a día. Para este paso te sugerimos tener:',
      items: [
        {
          icon: 'supports' as const,
          parts: [
            { text: 'Factura de proveedor:', em: true },
            {
              text: ' Una factura reciente de un proveedor a tu nombre.',
            },
          ],
        },
        {
          icon: 'accounts' as const,
          parts: [
            { text: 'Datos del negocio:', em: true },
            {
              text: ' Valor aproximado de activos y si has tenido crédito formal antes.',
            },
          ],
        },
      ],
    },
    /**intro hub PJ */
    'sobre-ti-juridica': {
      titulo: 'Sobre la empresa y tu',
      intro:
        'Para comenzar, necesitamos los datos básicos de representación y constitución de tu sociedad. Ten a la mano:',
      items: [
        {
          icon: 'id' as const,
          parts: [{ text: 'Información del representante', em: true }],
        },
        {
          icon: 'document' as const,
          parts: [
            { text: 'Identificación tributaria:', em: true },
            {
              text: ' Razón social, actividad económica y tipo de sociedad.',
            },
          ],
        },
        {
          icon: 'location' as const,
          parts: [
            { text: 'Ubicación de la operación:', em: true },
            {
              text: ' Dirección exacta y detalles de tu sede principal.',
            },
          ],
        },
        // Fotos: las agrega el modal según seccionUbicacion del catálogo.
      ],
    },
    /**
     * HU 8189 — intro ingresos PJ.
     */
    'sobre-ingresos-juridica': {
      titulo: 'Sobre los ingresos',
      intro:
        'Estos documentos son los que nos permiten presentarte formalmente ante los bancos aliados. Necesitarás cargar:',
      items: [
        {
          icon: 'document' as const,
          parts: [
            {
              text: 'Extractos bancarios, Declaración de renta, Declaraciones de IVA, Estados financieros.',
            },
          ],
        },
        // Fotos: las agrega el modal si están parametrizadas en esta sección.
      ],
    },
  },
  sobreTi: {
    heading: 'Datos personales',
    cedulaCta: 'Cargar archivo',
    cargarArchivos: 'Cargar archivos',
    cargarOtroArchivo: 'Cargar otro archivo',
    puedesSubirVarios: 'Puedes subir varios archivos',
    archivosCargados: 'Archivos cargados',
    cedulaArchivoSubido: 'Subido',
    cedulaArchivoAprobado: 'Revisado',
    cedulaArchivoRechazado: 'Rechazado',
    cedulaArchivoQuitar: 'Quitar archivo',
    placeholderSelect: 'Selecciona una opción',
    placeholderFecha: 'dd/mm/aaaa',
    validaciones: {
      campoObligatorio: 'Campo es obligatorio',
      fechaNacimientoInvalida: 'La fecha de nacimiento no es correcta',
      fechaExpedicionMenor:
        'La fecha de expedición del documento no puede ser menor a la fecha de nacimiento',
      fechaExpedicionIgual:
        'La fecha de expedición del documento no puede ser igual a la fecha de nacimiento',
      fechaExpedicionIncorrecta: 'La fecha de expedición del documento no es correcta',
      fechaMinima: 'La fecha ingresada no es válida',
      documentosInconsistentes:
        'Los documentos cargados presentan inconsistencias, por favor revise nuevamente',
    },
    campos: {
      genero: 'Genero',
      fechaNacimiento: 'Fecha de nacimiento',
      fechaExpedicion: 'Fecha de expedición de documento',
      estadoCivil: 'Estado civil',
      nivelEstudios: 'Nivel de estudios',
      personasACargo: '# de personas a cargo',
      seguridadSocial: '¿Pagas seguridad social?',
      sisben: '¿Tienes Sisben?',
      gruposSisben: 'Grupos Sisbén',
      creditoFormal: '¿Has tenido un crédito formal antes?',
      fechaDesembolso: 'Indícanos la fecha del desembolso',
      si: 'Si',
      no: 'No',
    },
  },
  /** PJ — «Sobre la empresa y tu» (paso datos). Fotos/redes reutilizan sobreNegocio. */
  sobreEmpresa: {
    heading: 'Datos de la empresa',
    placeholderSelect: 'Selecciona una opción',
    placeholderTexto: 'Escribe aquí',
    placeholderFecha: 'dd/mm/aaaa',
    validaciones: {
      campoObligatorio: 'Campo es obligatorio',
      fechaRegistroCamaraInvalida:
        'La fecha de registro no cumple el tiempo mínimo de antigüedad requerido',
      documentosInconsistentes:
        'Los documentos cargados presentan inconsistencias, por favor revise nuevamente',
    },
    campos: {
      genero: 'Género',
      minoria: '¿Perteneces a alguna minoría?',
      minoriaDescripcion: '¿Qué tipo de minoría?',
      creditoFormal: '¿Has tenido un crédito formal antes?',
      fechaDesembolso: 'Indícanos la fecha del desembolso',
      razonSocial: 'Razón social',
      actividadEconomica: 'Actividad económica',
      tipoSociedad: 'Tipo de sociedad',
      tieneCamaraComercio: '¿Tienes Cámara de Comercio?',
      fechaRegistroCamara: 'Fecha registro Cámara de Comercio',
      departamento: 'Departamento',
      ciudad: 'Ciudad',
      localidad: 'Localidad',
      barrio: 'Barrio',
      direccion: 'Dirección',
      si: 'Si',
      no: 'No',
    },
  },
  sobreNegocio: {
    fotosHeading: 'Fotos de tu negocio',
    fotosHint:
      'Sube 4 fotos que nos ayuden a conocer mejor tu negocio. Si tu negocio es digital, puedes cargar imágenes de tu página web, tus herramientas de trabajo o del producto o servicio que ofreces.',
    fotosCta: 'Cargar archivo',
    fotosCtaMobile: 'Cargar',
    cargarArchivos: 'Cargar archivos',
    cargarOtroArchivo: 'Cargar otro archivo',
    puedesSubirVarios: 'Puedes subir varios archivos',
    archivosCargados: 'Archivos cargados',
    fotosArchivoSubido: 'Subido',
    fotosArchivoAprobado: 'Revisado',
    fotosArchivoRechazado: 'Rechazado',
    fotosArchivoQuitar: 'Quitar archivo',
    fotosItems: [
      'Interior del negocio',
      'Exterior o fachada',
      'Vista lateral derecha',
      'Vista lateral izquierda',
    ],
    docsOpcionalesHeading: 'Información adicional (opcional)',
    docsOpcionalesHint:
      'Estos archivos no son obligatorios, pero compartirlos puede ayudarnos a entender mejor tu negocio.',
    redesHeading: 'Redes sociales',
    redesHint:
      'Comparte el enlace a tus redes sociales o página web. Esto nos ayuda a conocer mejor tu actividad y presencia digital.',
    placeholderUrl: 'Pega el enlace aquí',
    placeholderSelect: 'Selecciona una opción',
    placeholderTexto: 'Escribe aquí',
    placeholderFecha: 'dd/mm/aaaa',
    nombreNegocioHint:
      'Nombre comercial con el que identificas tu negocio ante clientes y proveedores.',
    validaciones: {
      campoObligatorio: 'Campo es obligatorio',
      fechaRegistroCamaraInvalida:
        'La fecha de registro no cumple el tiempo mínimo de antigüedad requerido',
      documentosInconsistentes:
        'Los documentos cargados presentan inconsistencias, por favor revise nuevamente',
    },
    campos: {
      nombreNegocio: 'Nombre del negocio',
      departamento: 'Departamento',
      ciudad: 'Ciudad',
      localidad: 'Localidad',
      barrio: 'Barrio',
      direccion: 'Dirección',
      tipoVivienda: 'Tipo de vivienda',
      tieneRut: '¿Tienes RUT?',
      tieneCamaraComercio: '¿Tienes Camara de Comercio?',
      fechaRegistroCamara: 'Fecha registro Cámara de Comercio',
      si: 'Si',
      no: 'No',
    },
  },
  sobreIngresos: {
    cargarArchivo: 'Cargar archivo',
    cargarArchivos: 'Cargar archivos',
    cargarOtroArchivo: 'Cargar otro archivo',
    puedesSubirVarios: 'Puedes subir varios archivos',
    archivosCargados: 'Archivos cargados',
    archivoSubido: 'Subido',
    archivoAprobado: 'Revisado',
    archivoRechazado: 'Rechazado',
    archivoQuitar: 'Quitar archivo',
    placeholderTexto: 'Escribe aquí',
    placeholderMoneda: 'Ej: $10.000.00',
    placeholderSelect: 'Selecciona una opción',
    placeholderObservaciones:
      'Ej: Mi emprendimiento trabaja con campesinos directamente',
    autorizacionPrefijo: 'Autorizo la ',
    autorizacionEnlace: 'consulta y reporte a centrales de riesgo.',
    totalIngresos: 'Total de ingresos',
    totalGastos: 'Total de egresos',
    gastosGenerales: 'Gastos generales',
    gastosEspecificos: 'Gastos específicos',
    resumenIngresos: 'Ingresos',
    resumenGastos: 'Gastos',
    efectivoDisponible: 'Efectivo disponible',
    infoAdicionalHeading: 'Información adicional (opcional)',
    infoAdicionalHint:
      'Estos archivos no son obligatorios, pero compartirlos puede ayudarnos a entender mejor tu negocio.',
    docsAdicionalesTitle: 'Documentos adicionales',
    docsAdicionalesDesc:
      'Carga documentos extra como facturas, contratos, presentación de los productos.',
    validaciones: {
      campoObligatorio: 'Campo es obligatorio',
      documentosInconsistentes:
        'Los documentos cargados presentan inconsistencias, por favor revise nuevamente',
    },
    docs: {
      facturaProveedor: {
        title: 'Factura de un proveedor',
        description:
          'La factura debe ser reciente y estar a tu nombre. También puedes subir una recomendación comercial.',
        accept: '.jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf',
      },
      declaracionRenta: {
        title: 'Declaración de renta',
        description: 'Te recomendamos subir el archivo sin clave.',
        accept: '.jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf',
      },
      serviciosPublicos: {
        title: 'Copia de servicios públicos',
        description:
          'Sube una copia reciente de alguno de los servicios públicos de tu negocio.',
        accept: '.jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf',
      },
      docsAdicionales: {
        accept: '.jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf',
      },
    },
    campos: {
      anioActivos: 'Año de activos a reportar',
      valorActivos: 'Valor aprox. del negocio (activos)',
      ventasUltimoAnio: 'Ventas del último año',
      declaraRenta: '¿Debes declarar renta?',
      mesReporte: 'Mes de reporte de la información',
      ventasUltimoMes: '¿Cuánto vendiste el último mes?',
      otrosIngresos: 'Otros ingresos',
      fuenteOtrosIngresos: '¿A qué corresponden los otros ingresos?',
      pagoProveedores: 'Pago a proveedores',
      arriendo: 'Arriendo',
      administracion: 'Administración',
      serviciosPublicosNegocio: 'Servicios públicos del negocio',
      sueldoPropio: 'Mi sueldo',
      sueldoEmpleados: 'Sueldo de mi(s) ayudante(s)',
      planCelular: 'Plan de celular del negocio',
      tarjetaCredito: 'Tarjeta de crédito del negocio',
      tieneCreditos: '¿Tienes créditos o deudas del negocio?',
      cuotaCreditos: 'Cuota créditos del negocio',
      saldoDeudas: '¿Cuál es el saldo de los créditos o deudas?',
      otrosGastos: 'Otros gastos del negocio',
      fuenteOtrosGastos: '¿A qué corresponden los otros gastos?',
      gastosGeneralesMonto: 'Total de gastos',
      observaciones: 'Observaciones adicionales sobre tu negocio',
      si: 'Si',
      no: 'No',
    },
  },
} as const;

export function resolvePerfilAccionLabel(
  estado: PerfilSeccionEstado
): string {
  switch (estado) {
    case 'completo':
      return PERFIL_COPY.acciones.editar;
    case 'en_proceso':
      return PERFIL_COPY.acciones.retomar;
    case 'pendiente':
    default:
      return PERFIL_COPY.acciones.continuar;
  }
}
